import { liftOperatingWindows } from "./reachability";
// Position helpers for the "plan my next few hours" entry point: snapping a
// live GPS fix to the routing graph and deriving an honest planning window
// from the current resort-local time. Pure functions, fixture-testable.

import type { PlannerGraph, PlannerNode } from "./graph-types";
import {
  isEdgeTraversable,
  type EngineInputs,
  type EngineOptions,
} from "./engine-types";

const EARTH_RADIUS_M = 6371000;

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

export type SnapResult = {
  nodeId: string;
  distanceM: number;
  node: PlannerNode;
};

/** Default snap ceiling: beyond this the fix is off the network (wrong resort,
 * down in the valley town) and the caller should fall back to the base picker. */
export const MAX_SNAP_DISTANCE_M = 100;

/**
 * Nearest routable graph node to a GPS fix. Only nodes with at least one
 * outgoing edge qualify (a dead-end node would strand the search before its
 * first leg). Returns null when nothing lies within maxDistanceM.
 */
export function nearestPlannerNode(
  graph: PlannerGraph,
  lat: number,
  lng: number,
  maxDistanceM: number = MAX_SNAP_DISTANCE_M,
): SnapResult | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const hasOutgoing = new Set<string>();
  for (const e of graph.edges) hasOutgoing.add(e.from);

  let best: SnapResult | null = null;
  for (const node of graph.nodes) {
    if (!hasOutgoing.has(node.id)) continue;
    const d = haversineM(lat, lng, node.lat, node.lng);
    if (d <= maxDistanceM && (!best || d < best.distanceM)) {
      best = { nodeId: node.id, distanceM: Math.round(d), node };
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Auto start-node selection (connectivity-aware)
// ---------------------------------------------------------------------------

// How much farther (in metres) a non-base node must be before it loses to a
// labelled base: the start should prefer a named lift base / village. Mirrors
// the 0.5 km nudge the original nearest-node picker used.
const BASE_NUDGE_M = 500;

function buildForwardAdjacency(graph: PlannerGraph): Map<string, string[]> {
  const adj = new Map<string, string[]>();
  for (const e of graph.edges) {
    const list = adj.get(e.from);
    if (list) list.push(e.to);
    else adj.set(e.from, [e.to]);
  }
  return adj;
}

function reachableSet(adj: Map<string, string[]>, startId: string): Set<string> {
  const seen = new Set<string>([startId]);
  const stack = [startId];
  while (stack.length > 0) {
    const u = stack.pop() as string;
    for (const v of adj.get(u) ?? []) {
      if (!seen.has(v)) {
        seen.add(v);
        stack.push(v);
      }
    }
  }
  return seen;
}

function buildReverseAdjacency(graph: PlannerGraph): Map<string, string[]> {
  const radj = new Map<string, string[]>();
  for (const e of graph.edges) {
    const list = radj.get(e.to);
    if (list) list.push(e.from);
    else radj.set(e.to, [e.from]);
  }
  return radj;
}

/**
 * Every node from which a base node is reachable (reverse BFS seeded from all
 * bases). A descent whose landing is in this set is "finishable": the rider can
 * ride it and still get to a base to end the day.
 */
function nodesThatCanReachABase(graph: PlannerGraph, radj: Map<string, string[]>): Set<string> {
  const seen = new Set<string>(graph.nodes.filter((n) => n.baseLabel).map((n) => n.id));
  const stack = [...seen];
  while (stack.length > 0) {
    const u = stack.pop() as string;
    for (const v of radj.get(u) ?? []) {
      if (!seen.has(v)) {
        seen.add(v);
        stack.push(v);
      }
    }
  }
  return seen;
}

/** Descents (non-climb trail edges) whose start is in `fromSet` and whose landing is in `toSet`. */
function countDescentsBetween(graph: PlannerGraph, fromSet: Set<string>, toSet: Set<string>): number {
  let count = 0;
  for (const e of graph.edges) {
    if (e.kind === "trail" && !e.climb && fromSet.has(e.from) && toSet.has(e.to)) count += 1;
  }
  return count;
}

function nearestNodeId(graph: PlannerGraph, resort?: { lat: number; lng: number }): string {
  let best = graph.nodes[0]?.id ?? "";
  if (!resort) return best;
  let bestScore = Infinity;
  for (const n of graph.nodes) {
    const score = haversineM(resort.lat, resort.lng, n.lat, n.lng) + (n.baseLabel ? 0 : BASE_NUDGE_M);
    if (score < bestScore) {
      bestScore = score;
      best = n.id;
    }
  }
  return best;
}

/**
 * The node to seed the auto plan from. Normally the labelled base nearest the
 * entry resort. But that nearest base is often stranded so that no full day can
 * be built — an isolated pocket (Solden, Nendaz), a base whose descents dead-end
 * (St Anton), or — the common one — a base that is OUTSIDE the area's
 * strongly-connected "ride and return" core, so a round trip back to it is
 * impossible even though one exists from a base inside the core (Zermatt's
 * nearest base sits in a 1-node cycle while a 15-node return loop exists nearby).
 *
 * So we pick, in order of preference: the nearest base from which the rider can
 * ride a descent AND get back to that same base (a real round trip — the default
 * expectation); else the nearest base that can at least finish point-to-point at
 * some base; else the nearest base. A base that already supports a round trip is
 * returned unchanged, so this never moves the start for a healthy graph.
 */
export function chooseAutoStartNode(
  graph: PlannerGraph,
  resort?: { lat: number; lng: number },
): string {
  const baseline = nearestNodeId(graph, resort);
  if (graph.nodes.length === 0) return baseline;

  const adj = buildForwardAdjacency(graph);
  const radj = buildReverseAdjacency(graph);
  const canReachBase = nodesThatCanReachABase(graph, radj);

  // Round-trip-usable descents: rideable from here AND the landing can get back
  // here (so returning to the exact start is possible). > 0 ⇔ a real return loop.
  const returnableFrom = (id: string) =>
    countDescentsBetween(graph, reachableSet(adj, id), reachableSet(radj, id));
  // Finishable descents: rideable from here and the landing can reach some base.
  const finishableFrom = (id: string) =>
    countDescentsBetween(graph, reachableSet(adj, id), canReachBase);

  const bases = graph.nodes.filter((n) => n.baseLabel);
  const candidates = bases.length > 0 ? bases : graph.nodes;

  // Keep the nearest base when it already scores (locality, no change for a
  // healthy graph); otherwise the highest-scoring base, nearest as the tie-break.
  // Null when nothing scores, so the caller can fall through to a weaker metric.
  const bestStartBy = (score: (id: string) => number): string | null => {
    if (score(baseline) > 0) return baseline;
    let best: string | null = null;
    let bestScore = 0;
    let bestDist = Infinity;
    for (const n of candidates) {
      const s = score(n.id);
      if (s === 0) continue;
      const d = resort ? haversineM(resort.lat, resort.lng, n.lat, n.lng) : 0;
      if (s > bestScore || (s === bestScore && d < bestDist)) {
        bestScore = s;
        bestDist = d;
        best = n.id;
      }
    }
    return best;
  };

  return bestStartBy(returnableFrom) ?? bestStartBy(finishableFrom) ?? baseline;
}

/**
 * Base nodes ranked best-first as fallback starts when the auto start builds
 * nothing: most round-trip-usable descents first, then most point-to-point
 * finishable. Bases that can do neither are dropped. The engine (not this
 * topology) decides what actually builds — this only orders which bases the
 * fresh-plan fallback tries, so a richer, returnable base is tried before a thin
 * one. (Topology over-counts: a base can look returnable yet build nothing once
 * scores, ability and time apply, which is why the caller verifies each by
 * actually planning from it.)
 */
export function rankBasesByReturnable(graph: PlannerGraph): string[] {
  const adj = buildForwardAdjacency(graph);
  const radj = buildReverseAdjacency(graph);
  const canReachBase = nodesThatCanReachABase(graph, radj);
  const scored = graph.nodes
    .filter((n) => n.baseLabel)
    .map((n) => {
      const reachable = reachableSet(adj, n.id);
      const returnable = countDescentsBetween(graph, reachable, reachableSet(radj, n.id));
      const finishable = countDescentsBetween(graph, reachable, canReachBase);
      return { id: n.id, score: returnable * 1_000_000 + finishable };
    })
    .filter((b) => b.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.map((b) => b.id);
}

/** Resort-local decimal hour for an instant. */
export function localDecimalHour(now: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
  return get("hour") + get("minute") / 60;
}

function toHm(decimalHour: number): string {
  const clamped = Math.max(0, Math.min(23.99, decimalHour));
  const h = Math.floor(clamped);
  const m = Math.round((clamped - h) * 60);
  if (m === 60) return `${String(h + 1).padStart(2, "0")}:00`;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Earliest sensible start when planning before the lifts spin. */
const DEFAULT_OPEN_HOUR = 9;
/** Below this many hours the plan would be a single run; tell the user. */
const MIN_WINDOW_H = 0.75;

export type NextHoursWindow =
  | {
      ok: true;
      startTime: string; // HH:MM resort-local
      endTime: string;
      startHour: number;
      endHour: number;
      /** True when the horizon was cut short by lift close. */
      cappedByClose: boolean;
      /** True when a pedal-only horizon was cut short by daylight. */
      cappedByDaylight: boolean;
      /** True when the start was pushed forward to lift opening. */
      delayedToOpen: boolean;
    }
  | { ok: false; reason: "too-late"; closeHour: number };

/** Latest closing hour across the day's running lifts (the honest end cap). */
export function latestLiftCloseHour(inputs: EngineInputs, fallback = 16.5): number {
  let latest = -Infinity;
  const downloads = new Set(inputs.graph.edges.flatMap(edge => edge.kind === "lift" && edge.direction === "down" ? [edge.id] : []));
  for (const [id, info] of inputs.liftInfo) {
    const status = inputs.liftStatus.get(id) ?? "unknown";
    if (status === "closed" || status === "hold") continue;
    const windows = [
      ...(info.operatingWindows ?? [{ open: info.openHour, close: info.closeHour }]),
      ...(downloads.has(id) ? info.downloadOperatingWindows ?? [] : []),
    ];
    for (const window of windows) if (window.open < window.close) latest = Math.max(latest, window.close);
  }
  return Number.isFinite(latest) ? latest : fallback;
}

export type LiftOperatingWindow = {
  openHour: number;
  closeHour: number;
  hasUsableLift: boolean;
};

/** Earliest opening and latest closing hour across lifts the rider may use. */
export function liftOperatingWindow(
  inputs: EngineInputs,
  opts: EngineOptions,
): LiftOperatingWindow {
  let earliest = Infinity;
  let latest = -Infinity;

  for (const edge of inputs.graph.edges) {
    if (edge.kind !== "lift") continue;
    if (!isEdgeTraversable(edge, inputs, opts)) continue;
    const windows = liftOperatingWindows(edge, inputs);
    for (const window of windows ?? []) {
      if (window.open >= window.close) continue;
      earliest = Math.min(earliest, window.open);
      latest = Math.max(latest, window.close);
    }
  }

  if (!Number.isFinite(earliest) || !Number.isFinite(latest)) {
    return {
      openHour: DEFAULT_OPEN_HOUR,
      closeHour: DEFAULT_OPEN_HOUR,
      hasUsableLift: false,
    };
  }

  return {
    openHour: earliest,
    closeHour: latest,
    hasUsableLift: true,
  };
}

/**
 * The honest planning window for "I have N hours from now": starts no earlier
 * than lift opening, ends no later than the last lift close, and refuses
 * windows too short to plan rather than emitting a one-leg plan.
 */
export function nextHoursWindow(
  nowLocalHour: number,
  horizonHours: number,
  closeHour: number,
  openHour: number = DEFAULT_OPEN_HOUR,
  limitKind: "lift-close" | "daylight" = "lift-close",
): NextHoursWindow {
  const start = Math.max(nowLocalHour, openHour);
  const rawEnd = start + horizonHours;
  const end = Math.min(rawEnd, closeHour);
  if (end - start < MIN_WINDOW_H) {
    return { ok: false, reason: "too-late", closeHour };
  }
  return {
    ok: true,
    startTime: toHm(start),
    endTime: toHm(end),
    startHour: start,
    endHour: end,
    cappedByClose: rawEnd > closeHour + 1e-9 && limitKind === "lift-close",
    cappedByDaylight: rawEnd > closeHour + 1e-9 && limitKind === "daylight",
    delayedToOpen: nowLocalHour < openHour - 1e-9,
  };
}
